import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSubmitMessage } from 'src/hooks/api';
import { useClientConfig } from 'src/hooks/useClientConfig';
import PopularQuestion from './PopularQuestion';

import './PopularQuestions.sass';

export interface PopularQuestionsListProps {
	onChunk: (chunk: string) => void;
	onComplete: (fullText: string) => void;
}

const PopularQuestionsList = ({ onChunk, onComplete }: PopularQuestionsListProps) => {
	const intl = useIntl();
	const client = useClientConfig();
	const searchParam = useMemo(() => new URL(window.location.href).searchParams.get('search'), []);

	const submitInitialSearch = useSubmitMessage({
		userInput: searchParam || '',
		onChunk,
		onComplete
	});

	useEffect(() => {
		if (searchParam) {
			submitInitialSearch();

			const url = new URL(window.location.href);
			url.searchParams.delete('search');
			window.history.replaceState({}, '', url);
		}
	}, [searchParam, submitInitialSearch]);

	const popularQuestions = useMemo(() => {
		const [localeKey] = intl.locale.split('-');
		const browserLang = typeof navigator !== 'undefined' ? navigator.language.split('-')[0] : null;

		const perClient =
			client?.initialQuestions?.[intl.locale] ??
			client?.initialQuestions?.[localeKey] ??
			(browserLang && browserLang !== localeKey ? client?.initialQuestions?.[browserLang] : undefined) ??
			(client?.language ? client?.initialQuestions?.[client.language] : undefined);

		if (perClient && perClient.length > 0) return perClient;

		const array: string[] = [];
		let index = 1;

		while (true) {
			const textId = `popular-questions.${index}`;
			try {
				const { messages } = intl;
				if (messages && messages[textId]) {
					const text = intl.formatMessage({ id: textId });
					array.push(text);
					index++;
				} else {
					break;
				}
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
			} catch (error) {
				break;
			}
		}
		return array;
	}, [intl, client]);

	return (
		<div id="popular-questions">
			{popularQuestions.map((text, index) => (
				<PopularQuestion key={text + index} text={text} onChunk={onChunk} onComplete={onComplete} />
			))}
		</div>
	);
};

export default PopularQuestionsList;
